// The "Back" control at the top of every detail screen.
//
// It used to be a plain <Link> at a FIXED destination, so every route into a
// detail page came out at the same front page. An operator who opened a project
// from the Alerts worklist, from the Big clients lane, or from an Explorer list
// they had filtered, sorted and paged landed on the bare Explorer with all of it
// thrown away, and had to rebuild the view before they could open the next row.
//
// So a plain click now walks the router's history back one entry, which is
// where they actually came from. Landing on the right URL is only half of it: the
// list is a fresh mount, so what it looked like has to be restored too. Three
// things do that, and all three are needed for the control to be worth clicking:
// the router restores the SCROLL offset (`scrollRestoration`), the URL carries
// the filters that belong in a link somebody can send (`?q=`, `?stage=`,
// `?status=`, `?from=`/`?to=`), and `@recruiter/shared`'s view-state cache
// restores the rest — the sort, the ticked filter values, and how far a "Show
// more" window had been grown — keyed by the same history entry the router keys
// scroll by (`components/view-state-scope.tsx`).
//
// The declared `to` survives as the FALLBACK, for the case with nothing behind it: a
// Slack deep link (`recruiter/alerts.py::_project_link`), a pasted URL, a fresh
// tab. `canGoBack()` is exactly that question — it reads the router's own
// history index, which starts at 0 for the first entry in a tab, so a back hop
// can never walk out of the console onto whatever page preceded it.
//
// It stays an ANCHOR rather than becoming a button, so the href is real:
// middle-click and ctrl/cmd-click still open the fallback list in a new tab
// (the gesture for keeping the list while reading a record), the browser still
// shows the destination on hover, and only the plain left click is intercepted.
// `lib/back-navigation.ts` owns which click is which.
import { Link, useRouter, useRouterState } from "@tanstack/react-router";

import { backLabel, backTarget } from "../lib/back-navigation";
import { ArrowRightIcon } from "./icons";
import "./back-link.css";

/** The pages a detail screen may fall back to. A literal union rather than the
 *  router's whole path type: a fallback is the LIST a record belongs to, and
 *  there are only ever a handful of those. */
export type BackFallback = "/explorer" | "/talent-management/searches";

export function BackLink({ to, label }: { to: BackFallback; label: string }) {
  const router = useRouter();
  // Read through router state rather than once per render: whether there is an
  // entry behind us is a question about a stack that moves under a component
  // which stays mounted across a search-param change.
  const canGoBack = useRouterState({ select: () => router.history.canGoBack() });

  return (
    <Link
      to={to}
      className="ad-back"
      onClick={(event) => {
        if (backTarget(event, canGoBack) !== "history") return;
        // TanStack composes our handler AHEAD of its own and stops at a
        // prevented event, so this is what keeps the Link from also navigating
        // to `to` on top of the hop we just made.
        event.preventDefault();
        router.history.back();
      }}
    >
      <ArrowRightIcon size={16} /> {backLabel(label, canGoBack)}
    </Link>
  );
}
